/**
 * What the public IP quota check says, as the Servers step needs it. Pure helpers around `GET /infrastructure/public-ip-quota/` (the backend's
 * rules/public_ip.py is authoritative; it also checks again when the deployment is dispatched, and the pipeline once more before Terraform runs).
 *
 * The rule is "never silently allow": while a public IP is wanted, the Servers step continues only on an answer that says the quota is there.
 * Waiting for the answer, an answer that says it is not, an answer that says Azure could not be read, and no answer at all (the request
 * failed) each keep Next disabled and put a message in "To continue". The user's way out is to ask again or to continue without a public IP.
 */
import { PUBLIC_IP_QUOTA_CHECKING, PUBLIC_IP_QUOTA_REQUEST_FAILED } from '@/data/publicIp'
import type { PublicIpQuota } from '@/types/infrastructure'

export type PublicIpQuotaState =
  /** No public IP is wanted (or there is no environment and region to ask about): nothing is checked. */
  | { kind: 'none' }
  /** Waiting for the first answer for these servers and this region. */
  | { kind: 'checking' }
  /** The quota is there. */
  | { kind: 'sufficient' }
  /** The backend answered that it is not (`failed: false`), or that Azure could not be read (`failed: true`): `message` is its own, to show as given. */
  | { kind: 'short'; failed: boolean; message: string }
  /** The request itself failed, so nothing is known: the portal's own constant says so. */
  | { kind: 'unreachable'; message: string }

export interface PublicIpQuotaAnswer {
  /** A public IP is wanted, and there is an environment and region to ask about. */
  wanted: boolean
  isError: boolean
  data: PublicIpQuota | undefined
}

export function publicIpQuotaState({ wanted, isError, data }: PublicIpQuotaAnswer): PublicIpQuotaState {
  if (!wanted) return { kind: 'none' }
  if (data) {
    if (data.sufficient) return { kind: 'sufficient' }
    // An answer that is not sufficient always has its reason; if it ever had none, the generic message is said rather than nothing.
    return { kind: 'short', failed: data.failed, message: data.message.trim() || PUBLIC_IP_QUOTA_REQUEST_FAILED }
  }
  // No answer yet: the first one is on its way, unless the request failed. Either way nothing is known, so nothing is allowed.
  return isError ? { kind: 'unreachable', message: PUBLIC_IP_QUOTA_REQUEST_FAILED } : { kind: 'checking' }
}

/** What keeps the Servers step from continuing, for "To continue". Nothing when no public IP is wanted or the quota is there. */
export function publicIpProblems(state: PublicIpQuotaState): string[] {
  switch (state.kind) {
    case 'checking':
      return [PUBLIC_IP_QUOTA_CHECKING]
    case 'short':
    case 'unreachable':
      return [state.message]
    default:
      return []
  }
}
